import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"

export interface ClientDetail {
  businesses?: Array<{ id: number; name?: string; logo?: string | null }>
  users?: Array<{ id: number; email?: string; name_first?: string; name_last?: string; first_name?: string; last_name?: string }>
  [key: string]: unknown
}

const inflight = new Map<string, Promise<ClientDetail>>()
const resultCache = new Map<string, { value: ClientDetail; expiresAt: number }>()
const TTL_MS = 30_000

/**
 * Fetch client detail with `include_archived=true`, deduping concurrent callers by clientId
 * and caching the resolved value for a short TTL.
 *
 * - Concurrent callers share the same in-flight Promise.
 * - Sequential callers within the TTL window get the cached value (no network).
 * - After TTL expires, the next call fires a fresh request.
 *
 * Note: the caller's `signal` is intentionally ignored so that one component's unmount
 * doesn't abort a shared fetch the other component is also awaiting. React Strict Mode's
 * mount/unmount/remount cycle would otherwise cause duplicate requests.
 */
export function fetchClientDetail(clientId: string): Promise<ClientDetail> {
  const cached = resultCache.get(clientId)
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.value)
  }

  const existing = inflight.get(clientId)
  if (existing) return existing

  const promise = httpJson<ClientDetail>(
    `${CRM_API_BASE}/client/${clientId}/?include_archived=true`,
  )
    .then((value) => {
      resultCache.set(clientId, { value, expiresAt: Date.now() + TTL_MS })
      return value
    })
    .finally(() => {
      inflight.delete(clientId)
    })

  inflight.set(clientId, promise)
  return promise
}

/** Manually invalidate the cached client detail (e.g., after editing the client). */
export function invalidateClientDetail(clientId: string): void {
  resultCache.delete(clientId)
  inflight.delete(clientId)
}
